import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Table from '@components/table';
import { FROST_PROBE_CONFIG, REST_API } from '@awp/constants';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import SoilProfileService from '@components/WeatherStationAdmin/Placement/SoilProfile/SoilProfileService';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { getSelectedPlacement } from '@stores/sensorEquipmentPacketSlice';

const isLayerEmpty = (layer) => {
  return !layer.SoilTypeId && (layer.TopmostRelativeElevation === '' || layer.TopmostRelativeElevation === undefined);
};

const isLayerComplete = (layer) => {
  return layer.SoilTypeId && layer.TopmostRelativeElevation !== undefined && layer.TopmostRelativeElevation !== '';
};

const EditableSoilProfile = ({ onValidationChange, onSaveChanges, saveTrigger, layers, setLayers }) => {
  const { soilTypes } = useSelector((state) => state.soilTypes);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState(null);

  const selectedPlacement = useSelector(getSelectedPlacement);

  const soilProfileService = new SoilProfileService();

  const fetchSoilProfileData = async () => {
    const url = `${REST_API.PLACEMENT_SOIL_LAYER}${selectedPlacement.SensorPlacementId}`;
    const response = await GetJson(url);
    const records = await response.json();
    setLayers(records);
  };

  useEffect(() => {
    if (selectedPlacement?.SensorTypeName === FROST_PROBE_CONFIG.TITLE_NAME && layers?.length === 0) {
      fetchSoilProfileData();
    }
  }, [selectedPlacement]);

  useEffect(() => {
    if (layers.length === 0) {
      handleAddLayer(-1);
    }
  }, [layers.length]);

  useEffect(() => {
    if (saveTrigger) {
      handleSaveAllLayers().then(() => {
        if (onSaveChanges) {
          onSaveChanges();
        }
      });
    }
  }, [saveTrigger]);

  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = Object.keys(errors).length > 0;
      onValidationChange(!hasErrors);
    }
  }, [errors, onValidationChange]);

  const validateLayers = (layersToValidate) => {
    const newErrors = {};
    const depthCounts = {};

    layersToValidate.forEach((layer) => {
      const depthValue = parseFloat(layer.TopmostRelativeElevation);
      if (!isNaN(depthValue)) {
        depthCounts[depthValue] = (depthCounts[depthValue] || 0) + 1;
      }
    });

    layersToValidate.forEach((layer) => {
      if (!layer.SoilTypeId) {
        newErrors[`soilType-${layer.PlacementSoilLayerId}`] = 'Soil type is required.';
      }

      const depthValue = parseFloat(layer.TopmostRelativeElevation);
      if (layer.TopmostRelativeElevation === '' || layer.TopmostRelativeElevation === undefined || isNaN(depthValue)) {
        newErrors[`depth-${layer.PlacementSoilLayerId}`] = 'Depth is required.';
      } else if (depthValue > 0) {
        newErrors[`depth-${layer.PlacementSoilLayerId}`] = 'Depth must be 0 or negative.';
      } else if (depthCounts[depthValue] > 1) {
        if (!newErrors[`duplicate-depth-${depthValue}`]) {
          newErrors[
            `duplicate-depth-${depthValue}`
          ] = `Duplicate depth value (${depthValue} cm) detected. Each layer must have a unique depth.`;
        }
      }
    });

    const incompleteLayers = layersToValidate.filter((layer) => !isLayerComplete(layer));
    if (incompleteLayers.length > 0) {
      newErrors['node-incomplete'] = 'All existing layers must be fully completed before adding a new one.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateLayer = (updatedLayer) => {
    setLayers((prev) => {
      const updatedLayers = prev.map((layer) =>
        layer.PlacementSoilLayerId === updatedLayer.PlacementSoilLayerId ? updatedLayer : layer,
      );
      validateLayers(updatedLayers);
      return updatedLayers;
    });
  };

  const handleAddLayer = (index) => {
    const isValid = validateLayers(layers);
    if (!isValid) {
      return;
    }

    const newLayer = {
      PlacementSoilLayerId: `temp-${Date.now()}`,
      SoilTypeId: '',
      TopmostRelativeElevation: '',
      SensorPlacementId: selectedPlacement.SensorPlacementId,
    };

    setLayers((prev) => {
      if (index === -1) return [newLayer, ...prev];
      const newArr = [...prev];
      const insertIndex = index + 1;
      newArr.splice(insertIndex, 0, newLayer);
      return newArr;
    });
  };

  const handleDeleteLayer = (layerId) => {
    const layer = layers.find((l) => l.PlacementSoilLayerId === layerId);
    if (!isLayerEmpty(layer)) {
      setShowDeleteConfirm(true);
      setSelectedLayer(layer);
      return;
    }

    confirmDelete();
  };

  const confirmDelete = async () => {
    const layerId = selectedLayer?.PlacementSoilLayerId;

    try {
      const updatedLayers = isValueEmpty(layerId)
        ? layers.filter((layer) => !isLayerEmpty(layer))
        : layers.filter((l) => l?.PlacementSoilLayerId !== layerId);
      setLayers(updatedLayers);
      setShowDeleteConfirm(false);
      setSelectedLayer(null);
      validateLayers(updatedLayers);
    } catch (err) {
      setErrors((prev) => ({ ...prev, general: 'Failed to delete layer.' }));
    }
  };

  const handleSaveAllLayers = async () => {
    // Filter empty layers
    const filteredLayers = layers.filter((layer) => !isLayerEmpty(layer));

    let isValid = validateLayers(filteredLayers);

    if (filteredLayers.length === 1 && filteredLayers[0]?.PlacementSoilLayerId.toString()?.startsWith('temp-')) {
      isValid = true;
    }

    if (!isValid) {
      return;
    }

    try {
      const layersToSync = filteredLayers.map((layer) => ({
        PlacementSoilLayerId:
          typeof layer.PlacementSoilLayerId === 'string' && layer.PlacementSoilLayerId.startsWith('temp-')
            ? null
            : layer.PlacementSoilLayerId,
        SoilTypeId: layer.SoilTypeId === '' ? null : layer.SoilTypeId,
        TopmostRelativeElevation: layer.TopmostRelativeElevation === '' ? 0 : layer.TopmostRelativeElevation,
        SensorPlacementId: layer.SensorPlacementId === '' ? null : layer.SensorPlacementId,
        ConcurrencyControlNumber: layer.ConcurrencyControlNumber || 0,
      }));

      const response = await PutJson(
        `${REST_API.PLACEMENT_SOIL_LAYER}${selectedPlacement.SensorPlacementId}`,
        layersToSync,
      );
      const result = await response.json();

      if (result.Errors && result.Errors.length > 0) {
        setErrors((prev) => ({
          ...prev,
          general: result.Errors.join(' '),
        }));
        return;
      } else if (response.status === 412) {
        setErrors(result);
        return;
      }

      const updatedLayers = result;
      setLayers(updatedLayers);
      setErrors({});
      return updatedLayers;
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        general: 'Failed to sync layers: ' + e.message,
      }));
      throw e;
    }
  };

  const soilTypeCell = (row) => {
    const layer = row.row.original;
    const options = soilTypes
      .filter((x) => x.IsActive === 'Y' || x.Id === layer.SoilTypeId)
      .map((type) => ({
        value: type.Id,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {type.PatternImage ? (
              <img
                src={`data:image/jpeg;base64,${type.PatternImage}`}
                alt={`${type.Code} pattern`}
                style={{ width: '30px', height: '30px', objectPosition: '0 0', objectFit: 'none' }}
              />
            ) : (
              <div className="no-pattern-select">No Pattern</div>
            )}
            <span>
              {type.Code} - {type.Description}
            </span>
          </div>
        ),
      }));

    return (
      <Select
        options={options}
        value={options.find((opt) => opt.value === layer.SoilTypeId)}
        onChange={(selected) => {
          const updatedLayer = { ...layer, SoilTypeId: selected.value };
          updateLayer(updatedLayer);
        }}
        placeholder="Select soil type..."
        styles={{
          control: (base) => ({ ...base, width: '300px' }),
        }}
        filterOption={(option, inputValue) => {
          const soilType = soilTypes?.find((type) => type.Id === option.value);

          if (!soilType) return false;

          return `${soilType.Code} - ${soilType.Description}`?.toLowerCase().includes(inputValue?.toLowerCase());
        }}
      />
    );
  };

  const depthCell = (row) => {
    const layer = row.row.original;
    const [inputValue, setInputValue] = useState(layer.TopmostRelativeElevation ?? '');

    useEffect(() => {
      setInputValue(layer.TopmostRelativeElevation ?? '');
    }, [layer.TopmostRelativeElevation]);

    const handleBlur = () => {
      const updatedLayer = { ...layer, TopmostRelativeElevation: inputValue };
      updateLayer(updatedLayer);
    };

    return (
      <input
        type="number"
        step="0.1"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        onBlur={handleBlur}
        style={{ width: '100px' }}
      />
    );
  };

  const actionsCell = (row) => {
    const layer = row.row.original;
    const hasErrors = Object.keys(errors).length > 0;
    return (
      <div className="d-flex gap-2 align-items-center">
        <span
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Add Layer Below"
          onClick={() =>
            !hasErrors && handleAddLayer(layers.findIndex((x) => x.PlacementSoilLayerId === layer.PlacementSoilLayerId))
          }
          className={hasErrors ? 'text-muted' : 'text-dark'}
          style={{ cursor: hasErrors ? 'not-allowed' : 'pointer' }}
        >
          <i className="fas fa-plus mx-1 my-1 awp-img-btn"></i>
        </span>
        <span
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title={String(layer.PlacementSoilLayerId).startsWith('temp-') ? 'Cancel New Layer' : 'Delete Layer'}
          onClick={() => handleDeleteLayer(layer.PlacementSoilLayerId)}
          className="text-dark"
          style={{ cursor: 'pointer' }}
        >
          <i className="fas fa-trash mx-1 my-1 awp-img-btn"></i>
        </span>
      </div>
    );
  };

  const columns = useMemo(
    () => soilProfileService.getColumns({ soilTypeCell, depthCell, actionsCell }),
    [soilTypes, layers],
  );

  if (selectedPlacement?.SensorTypeName !== FROST_PROBE_CONFIG.TITLE_NAME) {
    return <div className="soil-profile-empty"></div>;
  }

  const data = layers?.sort((a, b) => a?.TopmostRelativeElevation - b?.TopmostRelativeElevation) || [];

  return (
    <div className="soil-profile" style={{ margin: '10px 0 20px 0' }}>
      {Object.keys(errors).map((key) => (
        <div key={key} className="text-danger">
          {errors[key]}
        </div>
      ))}
      <Table columns={columns} data={data} fetchData={() => {}} customClass={'simplebar-soil'} />
      {showDeleteConfirm && (
        <AwpYesNoModal
          showYesNoModal={showDeleteConfirm}
          setShowYesNoModal={setShowDeleteConfirm}
          onClickYes={() => {
            confirmDelete();
          }}
          title="Delete"
          body="Are you sure you want to delete this layer?"
        />
      )}
    </div>
  );
};

export default EditableSoilProfile;
